/**
 * NEMA ElderCare Risk Assessment — questions + scoring
 * Source: NEMA_Eldercare_Risk_Assessment_Flow_Draft.docx
 *
 * Total Risk Score = Q1 + Q2 + Q3 + Q4 + Q5 (max 15)
 * 0–3 Low | 4–7 Moderate | 8–15 High
 */

export const MAX_ELDER_RISK_SCORE = 15;

export const ELDER_RISK_QUESTIONS = [
  {
    id: "fallHistory",
    icon: "fall",
    label: "Has the patient experienced a fall in the last 6 months?",
    options: [
      { value: "none", label: "No Falls", points: 0 },
      { value: "one_fall", label: "1 Fall", points: 2 },
      { value: "multiple_falls", label: "Multiple Falls / Fracture due to Fall", points: 3 },
    ],
  },
  {
    id: "memoryStatus",
    icon: "memory",
    label: "Does the patient have memory problems or confusion?",
    options: [
      { value: "none", label: "None", points: 0 },
      { value: "mild_forgetfulness", label: "Mild Forgetfulness", points: 1 },
      // Product override (hire flow): always maps to HIGH risk level.
      { value: "mild_aggressive_dementia", label: "Mild aggressive dementia", points: 1 },
      { value: "dementia_alz_wandering", label: "Dementia / Alzheimer's / Wandering", points: 3 },
    ],
  },
  {
    id: "mobilityTransfer",
    icon: "mobility",
    label: "How does the patient move around daily?",
    options: [
      { value: "independent", label: "Independent", points: 0 },
      { value: "walking_support", label: "Needs Walking Support / Walker", points: 1 },
      {
        value: "wheelchair_bedridden_transfer",
        label: "Wheelchair / Bedridden / Needs Transfer Assistance",
        points: 3,
      },
    ],
  },
  {
    id: "medicalCondition",
    icon: "medical",
    label: "Which best describes the patient's current medical condition?",
    options: [
      { value: "stable_chronic", label: "Stable Chronic Condition (BP, Diabetes etc.)", points: 0 },
      { value: "recovering_surgery_stroke", label: "Recovering after Surgery / Stroke", points: 2 },
      {
        value: "advanced_neuro_palliative",
        label: "Bedridden / Palliative / Advanced Neurological Disease",
        points: 3,
      },
    ],
  },
  {
    id: "dailyCareRequirement",
    icon: "care",
    label: "How much daily assistance does the patient require?",
    options: [
      { value: "basic_companionship", label: "Basic Companionship", points: 0 },
      {
        value: "bathing_toileting_feeding",
        label: "Needs Help with Bathing / Toileting / Feeding",
        points: 2,
      },
      {
        value: "complete_assistance",
        label: "Requires Complete Assistance Throughout the Day",
        points: 3,
      },
    ],
  },
];

export const INIT_ELDER_RISK_ANSWERS = Object.fromEntries(
  ELDER_RISK_QUESTIONS.map((q) => [q.id, null])
);

export const RISK_LEVEL_META = {
  low: {
    id: "low",
    label: "LOW RISK",
    title: "Low Risk Category Detected",
    summary:
      "The assessment indicates that your loved one is suitable for standard caregiver services.",
    recommendation: "Recommend General Caregiver",
    cta: "Continue to Caregiver Recommendations",
    certifiedBadge: "LOW RISK CERTIFIED",
  },
  moderate: {
    id: "moderate",
    label: "MODERATE RISK",
    title: "Moderate Risk Category Detected",
    summary:
      "The assessment indicates that your loved one may need additional support in certain areas of daily living. We recommend engaging trained caregivers with relevant experience.",
    recommendation: "Recommend Skilled/Experienced Caregiver",
    cta: "Continue to Caregiver Recommendations",
    certifiedBadge: "MODERATE RISK CERTIFIED",
  },
  high: {
    id: "high",
    label: "HIGH RISK",
    title: "High Risk Category Detected",
    summary:
      "The assessment indicates that your loved one needs a higher level of care and close monitoring. We recommend engaging trained caregivers with relevant experience in specialized care.",
    recommendation: "Recommend Hire a Care Manager before caregiver deployment",
    cta: "Initiate Care Manager Contact",
    certifiedBadge: "HIGH RISK CERTIFIED",
  },
};

function selectedOption(question, answerValue) {
  return question.options.find((o) => o.value === answerValue) || null;
}

export function computeRiskScore(riskAnswers = {}) {
  return ELDER_RISK_QUESTIONS.reduce((sum, q) => {
    const selected = selectedOption(q, riskAnswers[q.id]);
    return sum + (selected?.points ?? 0);
  }, 0);
}

/** Doc: <=3 LOW, 4–7 MODERATE, >=8 HIGH */
export function computeRiskLevel(riskScore, riskAnswers = {}) {
  if (riskAnswers.memoryStatus === "mild_aggressive_dementia") return "high";
  const score = Number(riskScore) || 0;
  if (score <= 3) return "low";
  if (score <= 7) return "moderate";
  return "high";
}

export function mapRiskAnswersToHealthAssessment(riskAnswers = {}) {
  const fallRisk = riskAnswers.fallHistory === null ? null : riskAnswers.fallHistory !== "none";
  const dementia =
    riskAnswers.memoryStatus === null
      ? null
      : riskAnswers.memoryStatus !== "none" && riskAnswers.memoryStatus !== "mild_forgetfulness";

  const mobilitySupport =
    riskAnswers.mobilityTransfer === null ? null : riskAnswers.mobilityTransfer !== "independent";

  const bedriddenFromMobility =
    riskAnswers.mobilityTransfer === null
      ? null
      : riskAnswers.mobilityTransfer === "wheelchair_bedridden_transfer";
  const bedriddenFromMedical =
    riskAnswers.medicalCondition === null
      ? null
      : riskAnswers.medicalCondition === "advanced_neuro_palliative";

  let bedridden = null;
  if (bedriddenFromMobility === null && bedriddenFromMedical === null) bedridden = null;
  else bedridden = Boolean(bedriddenFromMobility || bedriddenFromMedical);

  const dailyTasks =
    riskAnswers.dailyCareRequirement === null
      ? null
      : riskAnswers.dailyCareRequirement !== "basic_companionship";

  const chronicConditions =
    riskAnswers.medicalCondition === null ? null : riskAnswers.medicalCondition === "stable_chronic";

  return { fallRisk, dementia, bedridden, mobilitySupport, dailyTasks, chronicConditions };
}

export function flagsFromHealthAssessment(healthAssessment = {}) {
  const flags = [];
  if (healthAssessment.fallRisk) flags.push("fall_risk");
  if (healthAssessment.dementia) flags.push("dementia");
  if (healthAssessment.bedridden) flags.push("bedridden");
  if (healthAssessment.mobilitySupport) flags.push("mobility");
  if (healthAssessment.dailyTasks) flags.push("daily_tasks");
  if (healthAssessment.chronicConditions) flags.push("chronic");
  return flags;
}

export function attentionAreasFromHealthAssessment(healthAssessment = {}) {
  const areas = [];
  if (healthAssessment.fallRisk) areas.push("Fall Risk");
  if (healthAssessment.mobilitySupport) areas.push("Mobility Support");
  if (healthAssessment.dailyTasks) areas.push("Daily Task Assistance");
  if (healthAssessment.dementia) areas.push("Medication Assistance");
  if (healthAssessment.chronicConditions || healthAssessment.bedridden) areas.push("Health Monitoring");
  return areas;
}

export function buildRiskAssessment(riskAnswers = {}) {
  const riskScore = computeRiskScore(riskAnswers);
  const riskLevel = computeRiskLevel(riskScore, riskAnswers);
  const healthAssessment = mapRiskAnswersToHealthAssessment(riskAnswers);

  return {
    riskScore,
    riskLevel,
    highRisk: riskLevel === "high",
    riskAnswers,
    healthAssessment,
    flags: flagsFromHealthAssessment(healthAssessment),
    attentionAreas: attentionAreasFromHealthAssessment(healthAssessment),
    meta: RISK_LEVEL_META[riskLevel],
  };
}

export function normalizeStoredRiskAnswers(stored = {}) {
  const next = { ...INIT_ELDER_RISK_ANSWERS };
  ELDER_RISK_QUESTIONS.forEach((q) => {
    if (stored[q.id] != null) next[q.id] = stored[q.id];
  });
  return next;
}
